import { z } from 'zod';
/**
 * The blank-input behaviour differs per entry point (reject / becomes empty /
 * becomes null); everything past that is one shared sanitise-then-check
 * pipeline, so the three factories below differ only in this one decision.
 */
function sanitise(sanitiser, message, options, onBlank) {
    return (raw, ctx) => {
        if (raw.trim() === '')
            return onBlank(ctx);
        const cleaned = sanitiser(raw);
        if (cleaned === null) {
            ctx.addIssue({ code: 'custom', message });
            return z.NEVER;
        }
        if (options?.check && !options.check.test(cleaned)) {
            ctx.addIssue({ code: 'custom', message: options.check.message });
            return z.NEVER;
        }
        return cleaned;
    };
}
/**
 * Required sanitised text: blank is rejected with `message`, same as any
 * other value the sanitiser cannot coerce into canonical form.
 */
export function sanitisedText(sanitiser, message) {
    const schema = z.string().transform(sanitise(sanitiser, message, undefined, (ctx) => {
        ctx.addIssue({ code: 'custom', message });
        return z.NEVER;
    }));
    return schema;
}
/**
 * Sanitised text for a react-hook-form field: blank stays `''` (the field is
 * optional at the form boundary) and otherwise sanitises and validates,
 * surfacing the custom message directly as the field error.
 */
export function formFieldSanitisedText(sanitiser, message) {
    return z.string().transform(sanitise(sanitiser, message, undefined, () => ''));
}
/**
 * Sanitised text for a nullable, optional wire field: blank becomes `null`
 * (collapsing to SQL NULL, matching `nullableText`'s contract), and `null` /
 * `undefined` pass through unchanged via the baked-in `.optional().nullable()`.
 */
export function nullableSanitisedText(sanitiser, message, options) {
    const schema = z.string().transform(sanitise(sanitiser, message, options, () => null));
    return (options?.describe ? schema.describe(options.describe) : schema).optional().nullable();
}
export function nullableText(max, maxMessage) {
    const base = max === undefined
        ? z.string()
        : maxMessage === undefined
            ? z.string().max(max)
            : z.string().max(max, maxMessage);
    return base.transform(v => v.trim() === '' ? null : v).optional().nullable();
}
